/**
 * Guest list, as the host prints it — name, host-facing RSVP status, plus-one
 * relationship, email, phone. The paper answer to "who is on my list and are
 * they coming", for a host standing at check-in with no laptop.
 *
 * This module only builds the PRINTED table. The downloadable guest CSV is the
 * server's (`fetchTrackerExport`): it already resolves status the same way
 * and carries arrival, departure, room and notes that this view does not, and
 * a second client-side guest CSV under the same filename would be two files
 * whose columns drift apart.
 *
 * Status resolution is NOT re-derived here. `guestStatus` (lib/guestFilter.ts)
 * already resolves the two-source RSVP correctly — `going_override` from
 * `group_guest_tracking` first, `rsvp_status` from `group_invitees` only as a
 * fallback — and `GUEST_STATUS_LABELS` is the exact vocabulary the Guests
 * tab's own filter bar shows. Reading `rsvpStatus` directly here would repeat
 * the bug that told 40 RSVP'd guests they had never replied: see
 * `components/groups/broadcasts/audience.ts` for the same rule applied to
 * broadcast targeting.
 */
import { guestStatus, GUEST_STATUS_LABELS } from '../guestFilter';
import { orderGuestsWithPlusOnes } from '../trackerPresentation';
import type { TrackerGuest } from '../../services/tracker-api-client';

export interface GuestExportRow {
  name: string;
  status: string;
  /** The inviter's name, for a guest brought as someone's plus-one. '' for everyone else. */
  plusOneOf: string;
  email: string;
  phone: string;
}

/**
 * One row per guest, each invitee followed by the people they brought — the
 * same order the Guests tab renders, from the same helper.
 */
export function guestExportRows(guests: readonly TrackerGuest[]): GuestExportRow[] {
  const ordered = orderGuestsWithPlusOnes(guests);

  // Belt and braces: `orderGuestsWithPlusOnes` only descends one level, so a
  // plus-one recorded against another plus-one is not in `ordered` at all.
  // A printed check-in list that quietly lacks a guest is the one failure
  // this export must not have, so anyone the ordering helper did not place
  // is appended rather than lost.
  const placed = new Set(ordered.map(({ guest }) => guest.inviteeId));
  const byId = new Map(guests.map((guest) => [guest.inviteeId, guest]));
  const unplaced = guests
    .filter((guest) => !placed.has(guest.inviteeId))
    .map((guest) => ({
      guest,
      plusOneOf: (guest.plusOneOfInviteeId && byId.get(guest.plusOneOfInviteeId)?.fullName) || null,
    }));

  return [...ordered, ...unplaced].map(({ guest, plusOneOf }) => ({
    name: guest.fullName || 'Unnamed guest',
    status: GUEST_STATUS_LABELS[guestStatus(guest)],
    plusOneOf: plusOneOf ?? '',
    email: guest.email?.trim() ?? '',
    phone: guest.phone?.trim() ?? '',
  }));
}
